import React from 'react';
import { PageContainer } from '../layout/PageContainer';
import { ConfirmModal } from '../modals/ConfirmModal';
import { AdvancedJsonSchema } from './AdvancedJsonSchema';
import { SchemaTabProps } from './schema/types';
import { useSchemaState } from './schema/useSchemaState';
import { SchemaEvaluationModeCard } from './schema/SchemaEvaluationModeCard';
import { SchemaEditorToolbar } from './schema/SchemaEditorToolbar';
import { SchemaAttributesTable } from './schema/SchemaAttributesTable';

export type { SchemaTabProps } from './schema/types';

export const SchemaTab: React.FC<SchemaTabProps> = (props) => {
  const { table, canEdit } = props;

  const state = useSchemaState(props);

  return (
    <PageContainer variant="tab">
      <div className="space-y-6 text-xs font-sans">
        {/* Schema Evaluation Mode Card */}
        <SchemaEvaluationModeCard
          schemaMode={state.schemaMode}
          isSavingSchemaMode={state.isSavingSchemaMode}
          canEdit={canEdit}
          onSaveSchemaMode={state.handleSaveSchemaMode}
        />

        {/* Schema Editor Header & Filter Controls */}
        <div className="space-y-3">
          <SchemaEditorToolbar
            searchFilter={state.searchFilter}
            setSearchFilter={state.setSearchFilter}
            onExpandAll={state.handleExpandAll}
            onCollapseAll={state.handleCollapseAll}
            hasChanges={state.hasChanges}
            isSavingChanges={state.isSavingChanges}
            onDiscardChanges={state.handleDiscardChanges}
            onSaveChanges={state.handleSaveChanges}
            canEdit={canEdit}
            onAddNewAttribute={state.handleAddNewAttribute}
          />

          {/* High-Density Inline Schema Grid with Dedicated Columns */}
          <SchemaAttributesTable
            filteredColumns={state.filteredColumns}
            searchFilter={state.searchFilter}
            canEdit={canEdit}
            draggedColId={state.draggedColId}
            expandedRowIds={state.expandedRowIds}
            hasChanges={state.hasChanges}
            isSavingChanges={state.isSavingChanges}
            onDragStart={state.handleDragStart}
            onDragOver={state.handleDragOver}
            onDrop={state.handleDrop}
            onToggleExpand={state.toggleRowExpand}
            onUpdateField={state.handleUpdateField}
            onToggleRequired={state.handleToggleRequired}
            onUpdateValidationRules={state.handleUpdateValidationRules}
            onUpdateSampleValue={state.handleUpdateSampleValue}
            onAddSubfield={state.handleAddSubfield}
            onDeleteColumn={(col) => state.setColToDelete(col)}
            onUpdateSubfield={state.handleUpdateSubfield}
            onDeleteSubfield={state.handleDeleteSubfield}
            onSaveChanges={state.handleSaveChanges}
            onAddNewAttribute={state.handleAddNewAttribute}
          />

          {/* Collapsed Advanced Mode (JSON) */}
          <AdvancedJsonSchema
            columns={state.columnList}
            canEdit={canEdit}
            onApplyColumns={state.handleApplyFromAdvancedJson}
          />
        </div>
      </div>

      {/* Delete Column Confirmation Modal */}
      <ConfirmModal
        isOpen={Boolean(state.colToDelete)}
        onClose={() => state.setColToDelete(null)}
        onConfirm={state.confirmDeleteColumn}
        title="Remove Column Definition"
        message={
          <>
            Are you sure you want to remove attribute{' '}
            <strong className="text-cf-textPrimary font-semibold">{state.colToDelete?.name}</strong>{' '}
            (<code>{state.colToDelete?.key}</code>) from the schema? This will take effect when you save schema changes.
          </>
        }
        confirmLabel="Remove Attribute"
        isLoading={false}
      />
    </PageContainer>
  );
};
